import { PublicClientApplication } from '@azure/msal-browser'
import { AUTH_CONFIG } from '../config/auth'

export interface AuthUser {
  id: string
  email: string
  firstName: string
  lastName: string
  displayName: string
  role: string
  avatarUrl?: string
  preferences: {
    theme: 'light' | 'dark'
    motionPreference: 'full' | 'reduced' | 'none'
    defaultWorkspaceId?: string
  }
  workspaces: string[]
}

export interface AuthResponse {
  success: boolean
  data: {
    user: AuthUser
    tokens: {
      accessToken: string
      refreshToken?: string
      tokenType: string
      expiresAt?: number
      expiresIn?: number
    }
    /** Orgs the user belongs to, folded into the login response to save a
     *  round trip post-login (#218). Absent on older backends → client refetches. */
    organizations?: any[]
  }
}

export interface ApiError {
  success: false
  error: {
    code: string
    message: string
    timestamp: string
  }
}

export class AuthApiService {
  private baseUrl: string
  private msalInstance: PublicClientApplication
  private refreshInFlight: Promise<string | null> | null = null

  constructor(msalInstance: PublicClientApplication) {
    const origin = AUTH_CONFIG.API_BASE_URL || ''
    this.baseUrl = origin.endsWith('/api') ? origin : (origin ? `${origin}/api` : '/api')
    this.msalInstance = msalInstance
  }

  async authenticateWithAzure(): Promise<AuthResponse> {
    const accounts = this.msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      throw new Error('No authenticated accounts found. Please login first.')
    }

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: accounts[0] || null,
      })

      if (!response.idToken) {
        throw new Error('No ID token received from Azure AD')
      }

      return this.authenticateWithIdToken(response.idToken)
    } catch (error) {
      if (error instanceof Error && error.message.includes('InteractionRequiredAuthError')) {
        return this.authenticateInteractive()
      }
      throw error
    }
  }

  /**
   * Read a response body tolerantly. The backend (and intermediaries like the
   * rate limiter) can return non-JSON bodies — e.g. a 429 emits the plain text
   * "Too many requests...". Calling response.json() on that throws an opaque
   * "Unexpected token 'T'... is not valid JSON" error. This reads text first,
   * tries to parse JSON, and falls back to { message: text } so callers always
   * get a usable object and the real status drives the error message.
   */
  private async readBody(response: Response): Promise<any> {
    const text = await response.text().catch(() => '')
    if (!text) return {}
    try {
      return JSON.parse(text)
    } catch {
      return { message: text }
    }
  }

  async authenticateWithIdToken(idToken: string): Promise<AuthResponse> {
    if (!idToken) {
      throw new Error('ID token is required')
    }

    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken }),
    })

    const data = await this.readBody(response)

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Too many requests right now — please wait a moment and try again.')
      }
      throw new Error(data.error?.message || data.message || 'Authentication failed')
    }

    const authResponse = this.normalizeAuthResponse(data)
    this.storeAuthToken(authResponse.data.tokens.accessToken)
    return authResponse
  }

  private async authenticateInteractive(): Promise<AuthResponse> {
    const response = await this.msalInstance.acquireTokenPopup({
      scopes: ['User.Read'],
    })

    if (!response.idToken) {
      throw new Error('No ID token received from Azure AD')
    }

    return this.authenticateWithIdToken(response.idToken)
  }

  private normalizeAuthResponse(data: any): AuthResponse {
    const accessToken = data?.data?.tokens?.accessToken ?? data?.data?.token

    if (!accessToken || !data?.data?.user) {
      throw new Error('Authentication response was missing required fields')
    }

    return {
      success: Boolean(data?.success),
      data: {
        user: data.data.user,
        tokens: {
          accessToken,
          refreshToken: data?.data?.tokens?.refreshToken,
          tokenType: data?.data?.tokens?.tokenType ?? 'Bearer',
          expiresAt: data?.data?.tokens?.expiresAt,
          expiresIn: data?.data?.tokens?.expiresIn,
        },
        organizations: Array.isArray(data?.data?.organizations) ? data.data.organizations : undefined,
      },
    }
  }

  async getCurrentUser(): Promise<AuthUser> {
    const authHeaders = await this.getAuthHeader()

    if (!authHeaders.Authorization) {
      throw new Error('No authentication token found')
    }

    const response = await fetch(`${this.baseUrl}/auth/me`, {
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    })

    const data = await this.readBody(response)

    if (!response.ok) {
      if (response.status === 401) {
        this.clearAuthToken()
        throw new Error('Authentication expired')
      }
      if (response.status === 429) {
        throw new Error('Too many requests right now — please wait a moment and try again.')
      }
      throw new Error(data.error?.message || data.message || 'Failed to get user profile')
    }

    return data.data.user
  }

  async updateUserPreferences(preferences: Partial<AuthUser['preferences']>): Promise<AuthUser> {
    const token = this.getStoredToken()

    if (!token) {
      throw new Error('No authentication token found')
    }

    const response = await fetch(`${this.baseUrl}/auth/preferences`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(preferences),
    })

    const data = await this.readBody(response)

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Too many requests right now — please wait a moment and try again.')
      }
      throw new Error(data.error?.message || data.message || 'Failed to update preferences')
    }

    return data.data
  }

  async logout(): Promise<void> {
    this.clearAuthToken()

    const accounts = this.msalInstance.getAllAccounts()
    if (accounts.length > 0) {
      await this.msalInstance.logoutPopup({
        account: accounts[0] || null,
      })
    }
  }

  isAuthenticated(): boolean {
    const token = this.getStoredToken()
    if (token) {
      try {
        const tokenPart = token.split('.')[1]
        if (!tokenPart) throw new Error('Invalid token format')
        const payload = JSON.parse(atob(tokenPart))
        const currentTime = Math.floor(Date.now() / 1000)

        if (payload.exp && payload.exp < currentTime) {
          this.clearAuthToken()
          return false
        }

        return true
      } catch {
        this.clearAuthToken()
        return false
      }
    }

    return this.msalInstance.getAllAccounts().length > 0
  }

  getStoredToken(): string | null {
    return localStorage.getItem('ignis_auth_token')
  }

  getAccessToken(): string | null {
    return this.getStoredToken()
  }

  private storeAuthToken(token: string): void {
    localStorage.setItem('ignis_auth_token', token)
  }

  setAuthToken(token: string): void {
    this.storeAuthToken(token)
  }

  clearAuthToken(): void {
    localStorage.removeItem('ignis_auth_token')
  }

  async getAuthHeader(): Promise<Record<string, string>> {
    const storedToken = this.getStoredToken()
    if (storedToken) {
      return {
        Authorization: `Bearer ${storedToken}`,
      }
    }

    const accounts = this.msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      return {}
    }

    const configuredScopes = AUTH_CONFIG.AZURE_AD_SCOPES
      .split(/[\s,]+/)
      .map((scope) => scope.trim())
      .filter(Boolean)
    const apiScope = configuredScopes.find((scope) => scope.startsWith('api://'))
      || 'api://bbabaea04-452e-4aab-92c9-c3cbc4a0ae10/ignis-appmod-api'

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: [apiScope],
        account: accounts[0] || null,
      })

      return {
        Authorization: `Bearer ${response.accessToken}`,
      }
    } catch {
      return {}
    }
  }

  /**
   * Parse the exp claim from the stored backend JWT and return it as
   * milliseconds since epoch. Returns null if there is no stored token or the
   * token is not a well-formed JWT.
   */
  getTokenExpiryMs(): number | null {
    const token = this.getStoredToken()
    if (!token) return null
    try {
      const part = token.split('.')[1]
      if (!part) return null
      const payload = JSON.parse(atob(part))
      if (typeof payload.exp !== 'number') return null
      return payload.exp * 1000
    } catch {
      return null
    }
  }

  /**
   * Silently refresh the session using MSAL acquireTokenSilent → backend /auth/login.
   * Deduped: concurrent callers share a single in-flight promise.
   * Returns the fresh stored token on success, null on any failure.
   * NEVER triggers interactive login — silent only.
   */
  async refreshSessionSilently(): Promise<string | null> {
    if (this.refreshInFlight) return this.refreshInFlight

    this.refreshInFlight = (async (): Promise<string | null> => {
      try {
        const accounts = this.msalInstance.getAllAccounts()
        if (accounts.length === 0) return null

        const result = await this.msalInstance.acquireTokenSilent({
          scopes: ['User.Read'],
          account: accounts[0] || null,
        })
        if (!result.idToken) return null

        await this.authenticateWithIdToken(result.idToken)
        return this.getStoredToken()
      } catch {
        return null
      } finally {
        this.refreshInFlight = null
      }
    })()

    return this.refreshInFlight
  }

  /**
   * Return the stored token if it is still valid beyond bufferMs, otherwise
   * attempt a silent refresh and return whatever that yields.
   */
  async ensureValidToken(bufferMs = 120_000): Promise<string | null> {
    const expiry = this.getTokenExpiryMs()
    const token = this.getStoredToken()
    if (token && expiry !== null && expiry > Date.now() + bufferMs) {
      return token
    }
    return this.refreshSessionSilently()
  }

  getAuthHeaderSync(): Record<string, string> {
    const token = this.getStoredToken()
    if (!token) {
      return {}
    }

    return {
      Authorization: `Bearer ${token}`,
    }
  }
}

let authApiService: AuthApiService | null = null

export const createAuthApiService = (msalInstance: PublicClientApplication): AuthApiService => {
  if (!authApiService) {
    authApiService = new AuthApiService(msalInstance)
  }
  return authApiService
}

export const getAuthApiService = (): AuthApiService => {
  if (!authApiService) {
    throw new Error('AuthApiService not initialized. Call createAuthApiService first.')
  }
  return authApiService
}
